import { IconMap } from 'components/_const_assets/icons'
import { SubscriptionCapability, SubscriptionData, SubscriptionFeature, SubscriptionResponse, SubscriptionType } from 'components/_utils/subscriptionUtils'
import { datadogReportError } from 'events/datadog'
import { safeError } from 'events/error.utils'
import { jwtDecode } from 'jwt-decode'
import moment from 'moment'
import { Currency } from 'prices/types'
import { MembershipProducts } from 'products/allProducts'
import Stripe from 'stripe'
import { StripeManager } from 'stripe_lib'
import { STRIPE_PRODUCTS } from 'stripe_lib/products'
import { getRandomString } from './random'

export const RECHARGE_API_TOKEN = 'sk_2x2_ee2962c531da2e4a828e952e245ed3991910aa635d991da3618f9f6ff4238069'
export const RECHARGE_API_URL = 'https://api.rechargeapps.com'

export enum SubscriptionTier {
	Pro = 'pro',
	Plus = 'plus',
	Grandfathered = 'grandfathered',
}

export enum SubscriptionProvider {
	Stripe = 'stripe',
	Recharge = 'recharge',
	Unknown = 'unknown',
}

type SubscriptionTierType = {
	[key in SubscriptionTier]: string | null | undefined
}

type SubscriptionRechargeProductId = {
	[key in SubscriptionTier]: number | null | undefined
}

type SubscriptionPriceTierType = {
	[x: string]: SubscriptionTier
}

export const SubscriptionTierPrice: SubscriptionTierType = {
	[SubscriptionTier.Pro]: MembershipProducts.AutopilotStandard.variants.standard?.id,
	[SubscriptionTier.Plus]: MembershipProducts.AutopilotEnhanced.variants.standard?.id,
	[SubscriptionTier.Grandfathered]: null,
}

export const subscriptionTypeToStripePrice = (subscriptionType: SubscriptionType): string | null | undefined => {
	switch (subscriptionType) {
		case SubscriptionType.OPTIONAL_ERA_STANDARD:
			return STRIPE_PRODUCTS['8plus_standard__yearly']
		case SubscriptionType.OPTIONAL_ERA_PRO:
			return STRIPE_PRODUCTS['8plus_pro__yearly']
		case SubscriptionType.MANDATORY_ERA_STANDARD:
			return MembershipProducts.AutopilotStandard.variants.standard?.id
		case SubscriptionType.MANDATORY_ERA_ENHANCED:
			return MembershipProducts.AutopilotEnhanced.variants.standard?.id
		default:
			return null
	}
}

export const SubscriptionPriceTier: SubscriptionPriceTierType = {
	autopilot_standard__yearly: SubscriptionTier.Pro,
	autopilot_enhanced__yearly: SubscriptionTier.Plus,
}

export const SubscriptionTierShopifyRechargeId: SubscriptionRechargeProductId = {
	[SubscriptionTier.Pro]: 40218251133016, // Autopilot (Standard) variant ID in Shopify
	[SubscriptionTier.Plus]: 40218251165784, // Autopilot (Enhanced) variant ID in Shopify
	[SubscriptionTier.Grandfathered]: null, // Autopilot (Enhanced) variant ID in Shopify
}

export type SubscriptionStatus = Stripe.Subscription.Status | 'expired'

export enum SubscriptionPauseState {
	OneMonth = 1,
	ThreeMonths = 3,
	SixMonths = 6,
	Unpaused = 0,
}

export interface IRechargeCustomer {
	id: string
	first_name: string
	last_name: string
	email: string
	external_customer_id?: {
		ecommerce: string
	}
}

export interface IRechargePaymentMethod {
	id: number
	default: boolean
	payment_type: string
	payment_details: {
		brand: string
		exp_month: number
		exp_year: number
		last4: number
	}
}

export interface IRechargeSubscription {
	id: number
	customer_id: number
	status: string
	created_at: string
	cancelled_at: string
	cancellation_reason: string
	next_charge_scheduled_at: string
	product_title: string
	price: number
	properties: {
		name: string
		value: string
	}[]
}

export interface IRechargeCharge {
	id: number
	status: string
	type: string
	total_price: string
	created_at: string
	scheduled_at: string
	processed_at: string
	currency: Currency
}

export interface RechargeDiscount {
	/**
	 * Unique numeric identifier for the discount in Recharge.
	 */
	id: number

	/**
	 * An object encompassing data that limits discount application on the basis of an associated record (such as customer, products, etc.).
	 */
	applies_to: {
		/**
		 * A list of ids of the type indicated in applies_to.resource for which the discount can be applied. Currently only supports a list with one value.
		 */
		ids: number[]

		/**
		 * An indicator of whether the discount applies to onetimes, subscriptions, or all purchase item types.
		 */
		purchase_item_type: 'ALL' | 'ONETIME' | 'SUBSCRIPTION'

		/**
		 * An indicator of the type of resource to which applies_to_id refers.
		 */
		resource: 'product' | 'shopify_collection_id'
	}

	/**
	 * A list of channel objects containing information regarding discount behaviors for each.
	 */
	channel_settings: {
		/**
		 * An object containing api channel settings.
		 */
		api: {
			/**
			 * A boolean to indicate if the discount may be applied using the associated channel (defaults to true for all channels).
			 */
			can_apply: boolean
		}
		/**
		 * An object containing customer portal channel settings.
		 */
		customer_portal: {
			/**
			 * A boolean to indicate if the discount may be applied using the associated channel (defaults to true for all channels).
			 */
			can_apply: boolean
		}

		/**
		 * An object containing merchant portal channel settings.
		 */
		merchant_portal: {
			/**
			 * A boolean to indicate if the discount may be applied using the associated channel (defaults to true for all channels).
			 */
			can_apply: boolean
		}

		/**
		 * An object containing checkout channel settings.
		 */
		checkout_page: {
			/**
			 * A boolean to indicate if the discount may be applied using the associated channel (defaults to true for all channels).
			 */
			can_apply: boolean
		}
	}
	/**
	 * The code used to apply the discount.
	 */
	code: string

	/**
	 * The date and time when the discount was created.
	 */
	created_at: string

	/**
	 * The expiration timestamp of the discount. Past this time the Discount can no longer be redeemed. After ends_at the status of the Discount will go from active to disabled.
	 */
	ends_at: string

	/**
	 * An object containing external ids of the discount.
	 */
	external_discount_id: {
		ecommerce: string
	}

	/**
	 * A boolean to indicate if the discount can be used on checkout for customer that still don’t exist in Recharge database.
	 */
	first_time_customer_restriction: boolean

	/**
	 * The minimum cart subtotal needed for the discount to be applicable.
	 */
	prerequisite_subtotal_min: string

	/**
	 * The date when the discount becomes active.
	 */
	starts_at: string

	/**
	 * The status of the discount. Value can be:
* enabled discount is active to be applied
* disabled discount can’t be applied on new purchases. Discount will remain on existing charges to which it has already been applied.
* fully_disabled discount can no longer be applied. In addition, Discount is removed from every queued charge.

disabled and fully_disabled can both be reverted to enabled. However once a Discount has been updated to fully_disabled it will be removed from all Charges it had been applied to. This removal from Charges is irreversible. 
	 */
	status: 'enabled' | 'disabled' | 'fully_disabled'

	/**
	 * An object containing limitations on a discount based on usage_counts results
	 */
	usage_limits: {
		/**
		 * The discount can only be used once for a given customer checkout or manually applied once on the customer portal (irrespective of which address). Once applied, it will continue being re-applied to each new charge on the address.
		 */
		one_application_per_customer: boolean

		/**
		 * Discount can be used on checkout for customer that still don’t exist in Recharge database.
		 */
		first_time_customer_restriction: boolean

		/**
		 * The number of Charges the Discount will apply to besides the initial redemption for a given Customer. For example, if you want a discount to apply for 5 consecutive charges max_subsequent_redemptions should be 4.
		 */
		max_subsequent_redemptions: number
		/**
		 * Sets the limit on the number of times a discount can be used by all customers.
		 */
		redemptions: number
	}

	/**
	 * The date and time when the discount was last updated.
	 */
	updated_at: string

	/**
	 * The discounted value to be applied.
	 */
	value: number

	/**
	 * The type of discount mechanic.
	 */
	value_type: 'fixed_amount' | 'percentage' | 'shipping'
}

export interface IPaymentMethod {
	id: string
	brand: string
	last4: string
	exp_month: number
	exp_year: number
	brand_logo: string
	isDefault: boolean
	onlyCard?: boolean
}

export interface ICustomerResponse {
	success: boolean
	error?: unknown
	payment_methods?: IPaymentMethod[]
	default_payment_method?: string
}

export type CardBrand = 'amex' | 'diners' | 'discover' | 'eftpos_au' | 'jcb' | 'mastercard' | 'unionpay' | 'visa' | 'unknown' | 'shop_pay' | 'stripe link'

export interface SubscriptionDataWithType extends SubscriptionData {
	subscriptionType: SubscriptionType
	stripePaymentMethod?: string
	capabilities: SubscriptionCapability[]
}

export const getCardBrandLogo = (card_brand: string): string => {
	const brand = card_brand?.toLowerCase() as CardBrand
	if (brand === 'amex') return 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/card-logos/amex.png'
	if (brand === 'diners') return 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/card-logos/diners.png'
	if (brand === 'discover') return 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/card-logos/discover.png'
	if (brand === 'eftpos_au') return 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/card-logos/etfpos_au.png'
	if (brand === 'jcb') return 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/card-logos/jcb.png'
	if (brand === 'mastercard') return 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/card-logos/mastercard.png'
	if (brand === 'unionpay') return 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/card-logos/unionpay.png'
	if (brand === 'visa') return 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/card-logos/visa.png'
	if (brand === 'shop_pay') return 'https://vectorseek.com/wp-content/uploads/2023/09/Shop-Pay-Logo-Vector.svg-.png'
	if (brand === 'stripe link') return 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/link-by-stripe.jpg'
	return IconMap.credit_card
}

export const getCardBrandName = (card_brand: string): string => {
	const brand = card_brand?.toLowerCase() as CardBrand
	switch (brand) {
		case 'amex':
			return 'American Express'
		case 'diners':
			return 'Diners Club'
		case 'discover':
			return 'Discover'
		case 'eftpos_au':
			return 'EFTPOS'
		case 'jcb':
			return 'JCB'
		case 'mastercard':
			return 'Mastercard'
		case 'unionpay':
			return 'UnionPay'
		case 'visa':
			return 'Visa'
		case 'shop_pay':
			return 'Shop Pay'
		case 'stripe link':
			return 'Stripe Link'
		default:
			return 'Unknown'
	}
}

export const mapPlanFeatureToIcon = (feature: SubscriptionFeature): string => {
	if (feature === SubscriptionFeature.TEMPERATURE_DIAL) return IconMap.temperature
	if (feature === SubscriptionFeature.TEMPERATURE_SCHEDULE) return IconMap.temperature_schedule
	if (feature === SubscriptionFeature.AUTOMATIC_TEMPERATURE) return IconMap.temperature
	if (feature === SubscriptionFeature.VIBRATION_AND_THERMAL_ALARM) return IconMap.vibrate
	if (feature === SubscriptionFeature.SLEEP_AND_HEALTH_REPORTS) return IconMap.heart_report
	if (feature === SubscriptionFeature.SLEEP_AND_HEALTH_INSIGHTS) return IconMap.bar_chart
	if (feature === SubscriptionFeature.SNORING_DETECTION) return IconMap.snoring_detection
	if (feature === SubscriptionFeature.SNORING_MITIGATION) return IconMap.snoring_mitigation
	if (feature === SubscriptionFeature.TWO_YEAR_WARRANTY) return IconMap.warranty
	if (feature === SubscriptionFeature.FIVE_YEAR_WARRANTY) return IconMap.shield_check
	return IconMap.home
}

export const getSubscriptionTypeNickname = (type?: SubscriptionType): string => {
	if (type === SubscriptionType.NO_SUBSCRIPTION) return 'No Plan'
	if (type === SubscriptionType.TEMPORARY) return 'Temporary Trial Plan'
	if (type === SubscriptionType.OPTIONAL_ERA_CHURNED) return 'No Plan'
	if (type === SubscriptionType.PRE_SUB_ERA_GRANDFATHERED) return 'Legacy Plan'
	if (type === SubscriptionType.OPTIONAL_ERA_GRANDFATHERED) return 'Legacy Plan'
	if (type === SubscriptionType.OPTIONAL_ERA_STANDARD) return 'Standard Plan'
	if (type === SubscriptionType.OPTIONAL_ERA_PRO) return 'Pro Plan'
	if (type === SubscriptionType.MANDATORY_ERA_STANDARD) return 'Standard Autopilot Plan'
	if (type === SubscriptionType.MANDATORY_ERA_ENHANCED) return 'Enhanced Autopilot Plan'
	return 'No Plan'
}

export const subscriptionStatus = (
	subscription?: SubscriptionData | SubscriptionDataWithType | null
): {
	hasActiveSubscription: boolean
	hasExpiringSubscription: boolean
	hasExpiredSubscription: boolean
} => {
	if (subscription === null || subscription === undefined) return { hasActiveSubscription: false, hasExpiringSubscription: false, hasExpiredSubscription: false }

	const hasActiveSubscription = subscription.status === 'active' && subscription.subscriptionFrequency !== 'lifetime' && (subscription.willRenew || subscription.cancellationReason === 'temporary')
	const hasExpiringSubscription = subscription.status === 'active' && subscription.subscriptionFrequency !== 'lifetime' && !subscription.willRenew && subscription.cancellationReason !== 'temporary'
	const hasExpiredSubscription = subscription.status === 'canceled' && subscription.subscriptionFrequency !== 'lifetime' && !subscription.willRenew

	return {
		hasActiveSubscription,
		hasExpiringSubscription,
		hasExpiredSubscription,
	}
}

export const canChangePlan = (subscriptionType?: SubscriptionType): boolean => {
	if (!subscriptionType) return false
	return (
		subscriptionType === SubscriptionType.PRE_SUB_ERA_GRANDFATHERED ||
		subscriptionType === SubscriptionType.OPTIONAL_ERA_CHURNED ||
		subscriptionType === SubscriptionType.OPTIONAL_ERA_GRANDFATHERED ||
		subscriptionType === SubscriptionType.NO_SUBSCRIPTION ||
		subscriptionType === SubscriptionType.MANDATORY_ERA_ENHANCED
	)
}

type SubscriptionCurrencyPrice = {
	[key in Currency]: number
}

interface ISubscriptionPricing {
	[SubscriptionTier.Pro]: SubscriptionCurrencyPrice
	[SubscriptionTier.Plus]: SubscriptionCurrencyPrice
}

export const PLAN_PRICING: ISubscriptionPricing = {
	[SubscriptionTier.Pro]: {
		USD: 199,
		GBP: 199,
		EUR: 199,
		CHF: 199,
		CAD: 299,
		AUD: 299,
		SEK: 2199,
		DKK: 1399,
		AED: 780,
		MXN: 4000,
	},
	[SubscriptionTier.Plus]: {
		USD: 299,
		GBP: 299,
		EUR: 299,
		CHF: 299,
		CAD: 399,
		AUD: 499,
		SEK: 3299,
		DKK: 2099,
		AED: 1188,
		MXN: 6000,
	},
}

/**
 * Calculate the total unused amount of the current subscription
 * @param {string} renewal_date - next renewal date of the subscription in string format
 * @param {number} totalPrice - total price of the current subscription
 * @returns {number} - total unused amount of the current subscription, 0 if the subscription is already expired
 */
export const calculateProration = (renewal_date: string, totalPrice: number): number => {
	const daysUntilRenewal = moment(renewal_date).diff(moment(), 'days', true)
	if (daysUntilRenewal <= 0) return 0
	return (daysUntilRenewal / 365) * totalPrice // should we use 365 or 366?
}

export const addDefaultPaymentMethod = async (customer: Stripe.Customer): Promise<void> => {
	try {
		if (customer.invoice_settings?.default_payment_method !== null) {
			const payment_methods = await StripeManager.stripe.customers.listPaymentMethods(customer.id)
			const cardPaymentMethod = payment_methods.data.find((it) => it.card)
			if (cardPaymentMethod) {
				await StripeManager.stripe.customers.update(customer.id, {
					invoice_settings: {
						default_payment_method: cardPaymentMethod.id,
					},
				})
			}
		}
	} catch (error: unknown) {
		datadogReportError(safeError(error), 'In-app membership Adding default payment method')
	}
}

export const createStripeCustomer = async (email: string, firstName: string, lastName: string, metadata?: Stripe.Emptyable<Stripe.Metadata>): Promise<Stripe.Customer> => {
	return await StripeManager.stripe.customers.create({
		email: email,
		name: firstName + ' ' + lastName,
		description: 'Auto-created customer from in-app subscription management',
		metadata,
	})
}

export const findStripeCustomer = async (email: string): Promise<Stripe.Customer | null> => {
	try {
		const customers = await StripeManager.stripe.customers.list({ email: email, limit: 10, expand: ['data.invoice_settings'] })
		return customers?.data?.find((c) => c.invoice_settings?.default_payment_method) ?? customers?.data?.[0]
	} catch (error: unknown) {
		datadogReportError(safeError(error), 'In-app membership Find stripe customer')
		return null
	}
}

export const getUpgradeInfoBackend = async (jwt: string): Promise<SubscriptionResponse> => {
	const userId = jwtDecode(jwt).sub
	if (userId === undefined) {
		throw new Error('Invalid JWT')
	}
	const apiFetch = await fetch(`https://app-api.8slp.net/v1/users/${userId}/upgradeInfo/`, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${jwt}`,
		},
	})

	if (!apiFetch.ok) {
		throw new Error('Failed to fetch upgrade info')
	}

	const result = (await apiFetch.json()) as SubscriptionResponse
	return result
}

/**
 * https://developer.rechargepayments.com/2021-11/customers/customers_list
 */
export const fetchRechargeCustomer = async (email: string): Promise<IRechargeCustomer> => {
	const response = await fetch(`${RECHARGE_API_URL}/customers?email=${encodeURIComponent(email)}`, {
		headers: {
			'X-Recharge-Access-Token': RECHARGE_API_TOKEN,
			'Content-Type': 'application/json',
		},
	})

	if (!response.ok) {
		throw new Error('Failed to fetch Recharge customer')
	}

	const data = (await response.json()) as { customers: IRechargeCustomer[] }
	return data.customers?.[0]
}

/**
 * https://developer.rechargepayments.com/2021-11/payment_methods/payment_methods_list
 */
export const fetchRechargeCustomerPaymentMethods = async (customer_id: string): Promise<IRechargePaymentMethod[]> => {
	const response = await fetch(`${RECHARGE_API_URL}/payment_methods?customer_id=${customer_id}`, {
		headers: {
			'X-Recharge-Access-Token': RECHARGE_API_TOKEN,
			'Content-Type': 'application/json',
		},
	})

	if (!response.ok) {
		throw new Error('Failed to fetch Recharge customer payment methods')
	}

	const data = (await response.json()) as { payment_methods: IRechargePaymentMethod[] }
	return data.payment_methods
}

/**
 * https://developer.rechargepayments.com/2021-11/subscriptions/subscriptions_list
 */
export const fetchRechargeSubscriptions = async (customerId: string): Promise<IRechargeSubscription[]> => {
	const response = await fetch(`${RECHARGE_API_URL}/subscriptions?customer_id=${customerId}`, {
		headers: {
			'X-Recharge-Access-Token': RECHARGE_API_TOKEN,
			'Content-Type': 'application/json',
		},
	})

	if (!response.ok) {
		throw new Error('Failed to fetch Recharge subscriptions')
	}

	const data = (await response.json()) as { subscriptions: IRechargeSubscription[] }
	return data.subscriptions
}

/**
 * https://developer.rechargepayments.com/2021-11/subscriptions/subscriptions_retrieve
 */
export const fetchRechargeSubscription = async (subscriptionId: string): Promise<IRechargeSubscription> => {
	const response = await fetch(`${RECHARGE_API_URL}/subscriptions/${subscriptionId}`, {
		headers: {
			'X-Recharge-Access-Token': RECHARGE_API_TOKEN,
			'Content-Type': 'application/json',
		},
	})

	if (!response.ok) {
		throw new Error('Failed to fetch Recharge subscription')
	}

	const data = (await response.json()) as { subscription: IRechargeSubscription }
	return data.subscription
}

/**
 * https://developer.rechargepayments.com/2021-11/subscriptions/subscriptions_update
 */
export const upgradeRechargeSubscription = async (subscriptionId: string, currency: Currency = 'USD'): Promise<IRechargeSubscription> => {
	const response = await fetch(`${RECHARGE_API_URL}/subscriptions/${subscriptionId}/?force_update=true`, {
		method: 'PUT',
		headers: {
			'X-Recharge-Access-Token': RECHARGE_API_TOKEN,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			status: 'ACTIVE',
			quantity: 1,
			product_title: 'Autopilot (Plus)',
			price: PLAN_PRICING[SubscriptionTier.Plus][currency].toFixed(2),
			external_variant_id: SubscriptionTierShopifyRechargeId[SubscriptionTier.Plus],
		}),
	})

	if (!response.ok) {
		throw new Error('Failed to upgrade Recharge subscription')
	}

	const data = (await response.json()) as { subscription: IRechargeSubscription }
	return data.subscription
}

/**
 * https://developer.rechargepayments.com/2021-11/subscriptions/subscriptions_update
 */
export const downgradeRechargeSubscription = async (subscriptionId: string, currency: Currency = 'USD'): Promise<IRechargeSubscription> => {
	const subscription = await fetchRechargeSubscription(subscriptionId)
	const proration = subscription.next_charge_scheduled_at ? calculateProration(subscription.next_charge_scheduled_at, PLAN_PRICING[SubscriptionTier.Plus][currency]) : 0
	const priceWithProration = proration - PLAN_PRICING[SubscriptionTier.Pro][currency]

	const subscriptionUpdateResponse = await fetch(`${RECHARGE_API_URL}/subscriptions/${subscriptionId}/?force_update=true`, {
		method: 'PUT',
		headers: {
			'X-Recharge-Access-Token': RECHARGE_API_TOKEN,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			status: 'ACTIVE',
			quantity: 1,
			product_title: 'Autopilot (Pro)',
			price: PLAN_PRICING[SubscriptionTier.Pro][currency].toFixed(2),
			external_variant_id: SubscriptionTierShopifyRechargeId[SubscriptionTier.Pro],
		}),
	})

	if (!subscriptionUpdateResponse.ok) {
		throw new Error('Failed to downgrade Recharge subscription')
	}

	const data = (await subscriptionUpdateResponse.json()) as { subscription: IRechargeSubscription }
	const updatedSubscription = data.subscription

	const subscriptionChargesResponse = await fetch(`${RECHARGE_API_URL}/charges/?customer_id=${updatedSubscription.customer_id}&status=queued&sort_by=id-desc`, {
		method: 'GET',
		headers: {
			'X-Recharge-Access-Token': RECHARGE_API_TOKEN,
			'Content-Type': 'application/json',
		},
	})

	if (!subscriptionChargesResponse.ok) {
		throw new Error('Failed to fetch Recharge charges')
	}

	const subscriptionChargesResponseData = (await subscriptionChargesResponse.json()) as { charges: IRechargeCharge[] }
	const subscriptionCharges = subscriptionChargesResponseData.charges

	if (subscriptionCharges.length > 0 && priceWithProration > 0) {
		const chargeId = subscriptionCharges[0].id
		const discountCreateResponse = await fetch(`${RECHARGE_API_URL}/discounts`, {
			method: 'POST',
			headers: {
				'X-Recharge-Access-Token': RECHARGE_API_TOKEN,
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				code: getRandomString(10),
				discount_type: 'fixed_amount',
				value: priceWithProration,
				duration: 'usage_limit',
				duration_usage_limit: 1,
			}),
		})

		if (!discountCreateResponse.ok) {
			throw new Error('Failed to create Recharge discount')
		}

		const discountCreateResponseData = (await discountCreateResponse.json()) as { discount: RechargeDiscount }
		if (discountCreateResponseData.discount?.id) {
			const updateChargeResponse = await fetch(`${RECHARGE_API_URL}/charges/${chargeId}/apply_discount`, {
				method: 'POST',
				headers: {
					'X-Recharge-Access-Token': RECHARGE_API_TOKEN,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					discount_id: discountCreateResponseData.discount.id,
				}),
			})

			if (!updateChargeResponse.ok) {
				throw new Error('Failed to update Recharge charge')
			}
		}
	}

	return updatedSubscription
}

export const cancelRechargeSubscription = async (subscriptionId: string, send_email = true): Promise<IRechargeSubscription> => {
	const response = await fetch(`${RECHARGE_API_URL}/subscriptions/${subscriptionId}/cancel`, {
		method: 'POST',
		headers: {
			'X-Recharge-Access-Token': RECHARGE_API_TOKEN,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			cancellation_reason: 'user_initiated',
			send_email: send_email,
		}),
	})
	if (response.ok && response.status === 200) {
		const data = (await response.json()) as { subscription: IRechargeSubscription }
		return data.subscription
	}
	throw new Error('Failed to cancel Recharge subscription')
}

export const activateRechargeSubscription = async (subscriptionId: string): Promise<IRechargeSubscription> => {
	const response = await fetch(`${RECHARGE_API_URL}/subscriptions/${subscriptionId}/activate`, {
		method: 'POST',
		headers: {
			'X-Recharge-Access-Token': RECHARGE_API_TOKEN,
			'Content-Type': 'application/json',
		},
	})
	if (response.ok && response.status === 200) {
		const data = (await response.json()) as { subscription: IRechargeSubscription }
		return data.subscription
	}
	throw new Error('Failed to activate Recharge subscription')
}

export const setRechargeSubscriptionPause = async (subscriptionId: string, value: SubscriptionPauseState): Promise<IRechargeSubscription> => {
	const response = await fetch(`${RECHARGE_API_URL}/subscriptions/${subscriptionId}`, {
		method: 'PUT',
		headers: {
			'X-Recharge-Access-Token': RECHARGE_API_TOKEN,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			properties: [
				{
					name: 'pause',
					value: value,
				},
				{
					name: 'paused_on',
					value: value !== SubscriptionPauseState.Unpaused ? moment().toISOString() : null,
				},
			],
		}),
	})
	if (response.ok && response.status === 200) {
		const data = (await response.json()) as { subscription: IRechargeSubscription }
		return data.subscription
	}
	throw new Error('Failed to set Recharge subscription pause')
}
